import type { TaskSuggestion } from '@frontend/api-types';
import { Signature, Stamp } from '@frontend/web-icons';
import { Header, Modal, Text, XStack, YStack } from '@frontend/web-react/components';
import LetterRecommendationTaskSuggestion from '@frontend/web/components/DomainSpecific/LetterRecommendations/LetterRecommendationTaskSuggestion';
import {
  useCreateTaskContext,
  useMarkLetterRecommendationAsViewedMutation,
  useRetrieveClientQuery,
  useRetrieveLetterRecommendationQuery,
} from '@frontend/web/hooks';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

const sourceToHeadingMap = {
  calendar: 'From your calendar,',
  interests: 'Because you created similar tasks,',
};

export type GroupedTaskSuggestions = {
  recommendations: TaskSuggestion[];
  source: string;
};

export const groupTaskSuggestionsBySource = (suggestions: TaskSuggestion[]): GroupedTaskSuggestions[] => {
  return suggestions.reduce<GroupedTaskSuggestions[]>((acc, suggestion) => {
    const existingGroup = acc.find((group) => group.source === suggestion.source);
    if (existingGroup) {
      return acc.map((group) =>
        group.source === suggestion.source
          ? { ...group, recommendations: [...group.recommendations, suggestion] }
          : group,
      );
    } else {
      return [...acc, { recommendations: [suggestion], source: suggestion.source }];
    }
  }, []);
};

type LetterRecommendationDetailModalProps = {
  isOpen: boolean;
  letterRecommendationId: string;
  onClose: () => void;
};

export const LetterRecommendationDetailModal = ({
  isOpen,
  letterRecommendationId,
  onClose,
}: LetterRecommendationDetailModalProps) => {
  const { data: client } = useRetrieveClientQuery();
  const { data: letterRecommendation, refetch: refetchLetterRecommendation } =
    useRetrieveLetterRecommendationQuery(letterRecommendationId);
  const [markLetterRecommendationAsViewed] = useMarkLetterRecommendationAsViewedMutation();
  const { isCreateTaskModalOpen, openCreateTaskModal } = useCreateTaskContext();

  const groupedSuggestions: GroupedTaskSuggestions[] = useMemo(
    () => groupTaskSuggestionsBySource(letterRecommendation?.recommendations || []),
    [letterRecommendation],
  );

  useEffect(() => {
    if (!isCreateTaskModalOpen) {
      // isCreateTaskModalOpen updates before modal is fully closed, add delay to prevent overlapping
      setTimeout(() => setIsTaskCreationModalOpen(false), 250);
    }
  }, [isCreateTaskModalOpen]);

  const [showLetter, setShowLetter] = useState(false);
  const [shouldFadeIn, setShouldFadeIn] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isTaskCreationModalOpen, setIsTaskCreationModalOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (letterRecommendation) {
      if (!letterRecommendation.viewedAt) {
        setShowLetter(true);
        setShouldFadeIn(true);
        markLetterRecommendationAsViewed(letterRecommendation!.id);
      } else {
        setShowLetter(true);
        setShouldFadeIn(false);
      }
      setIsLoaded(true);
    }
  }, [letterRecommendation]);

  useEffect(() => {
    if (showLetter && shouldFadeIn && contentRef.current) {
      contentRef.current?.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: 1000,
        easing: 'ease-in-out',
        fill: 'forwards',
      });
    }
  }, [showLetter, shouldFadeIn]);

  if (!letterRecommendation || groupedSuggestions.length === 0 || isTaskCreationModalOpen) {
    return null;
  }

  const handleCreate = (suggestion: TaskSuggestion) => {
    setIsTaskCreationModalOpen(true);
    openCreateTaskModal({
      initialData: {
        description: suggestion.description,
        letterRecommendationId: letterRecommendation.id,
        letterRecommendationTaskSuggestionIdx: letterRecommendation.recommendations.findIndex(
          (rec) => rec === suggestion,
        ),
        name: suggestion.description,
      },
      isRecurrence: false,
      onCreateCallback: refetchLetterRecommendation,
      stepsToSkip: ['description'],
    });
  };

  return (
    <Modal
      Header={
        <Header
          // eslint-disable-next-line tailwindcss/no-arbitrary-value
          className={isLoaded ? 'paper-background paper-content' : 'h-[80px]'}
          onClickRightIcon={isLoaded ? onClose : undefined}
          rightIconName={isLoaded ? 'IconX' : undefined}
          title=""
        />
      }
      contentBorderRadius="rounded-lg"
      isOpen={isOpen}
      onClose={onClose}
      padding="lg"
      scrollViewContainerClassName={`min-w-[600px] min-h-[800px] max-w-[600px] max-h-[80%]
        ${isLoaded ? 'paper-background paper-content pt-none pb-none' : ''}`}
      size="600px"
    >
      {isLoaded ? (
        <YStack className="no-scroll-bar overflow-y-auto" gap="md" ref={contentRef}>
          <YStack gap="sm">
            <Text className="font-caveat text-textPrimary" type="h1">
              Hey {client?.nickname || client?.firstName},
            </Text>
            <Text className="text-textPrimary" type="p-md">
              {letterRecommendation?.preamble}{' '}
              {groupedSuggestions[0]?.source
                ? sourceToHeadingMap[groupedSuggestions[0].source as keyof typeof sourceToHeadingMap]
                : null}
            </Text>
          </YStack>
          {groupedSuggestions.map((group, groupIndex) => (
            <Fragment key={group.source}>
              {groupIndex > 0 && (
                <Text className="text-textPrimary" type="p-md">
                  {sourceToHeadingMap[group.source as keyof typeof sourceToHeadingMap]}
                </Text>
              )}
              {group.recommendations.map((suggestion, suggestionIndex) => (
                <LetterRecommendationTaskSuggestion
                  disabled={Boolean(suggestion.taskId || suggestion.recurrenceId || suggestion.draftTaskId)}
                  key={`${groupIndex}-${suggestionIndex}`}
                  onPress={() => handleCreate(suggestion)}
                  text={suggestion.description}
                />
              ))}
            </Fragment>
          ))}
          <YStack>
            <Text className="font-caveat text-textPrimary" type="h2">
              Signing off,
            </Text>
            <XStack className="flex items-center justify-center">
              <YStack>
                <Signature />
              </YStack>
              <YStack className="w-1/4 self-start">
                <Stamp />
              </YStack>
            </XStack>
          </YStack>
        </YStack>
      ) : null}
    </Modal>
  );
};

export default LetterRecommendationDetailModal;
